/**
 * AlertStatusCard
 * Partner or Client summary card shown in the "View more details" section.
 * All status logic is preserved exactly from the original AlertsAccordion.
 */
import React from 'react';
import {
  FaCheckCircle,
  FaTimesCircle,
  GrServerCluster,
  AiOutlineAudit,
} from '@/components/atoms/Icons';
import Spinner from '@/components/atoms/Spinner';
import type {
  ClientStatus,
  PartnerStatus,
} from '@/pages/Alert/alerts.interface';
import {
  RetryLabels,
  CardTitles,
  StatusIconType,
} from '@/services/alert/alerts.constants';
import {
  COMMON_STEPPER_CONTENT,
  STATUS_CODES,
  ORGANISM_PROPS,
} from '@/constant/global/global.constant';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import PillCell, {
  type PillConfig,
} from '@/components/organism/StatusPills/PillCell';
import { Text } from '@/components/atoms/AppText';
import { BsDashSquareFill } from 'react-icons/bs';

// ─── Colour → PillConfig ──────────────────────────────────────────────────────
const C: Record<string, PillConfig> = {
  green: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-400',
    dot: 'bg-emerald-500',
  },
  red: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    dot: 'bg-red-500',
  },
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-700 dark:text-indigo-400',
    dot: 'bg-indigo-500',
  },
  gray: {
    bg: 'bg-gray-100 dark:bg-gray-700/40',
    text: 'text-gray-600 dark:text-gray-400',
    dot: 'bg-gray-400',
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    dot: 'bg-amber-500',
  },
};

// ─── Status icon ──────────────────────────────────────────────────────────────
const StatusIcon = ({ type }: { type: StatusIconType }) => {
  switch (type) {
    case StatusIconType.SUCCESS:
      return <FaCheckCircle className="h-3.5 w-3.5 text-emerald-500" />;
    case StatusIconType.FAIL:
      return <FaTimesCircle className="h-3.5 w-3.5 text-red-500" />;
    case StatusIconType.LOADER:
      return <Spinner size="xs" color="indigo" />;
    case StatusIconType.HYPHEN:
      return <BsDashSquareFill className="h-3.5 w-3.5 text-indigo-500" />;
    default:
      return <Spinner size="xs" color="gray" />;
  }
};

// ─── Row helpers ──────────────────────────────────────────────────────────────
const StatusRow = ({
  label,
  icon,
  pill,
}: {
  label: string;
  icon?: React.ReactNode;
  pill: React.ReactNode;
}) => {
  return (
    <div className="flex flex-wrap items-center justify-between px-4 py-2.5 gap-2.5 sm:gap-3">
      <Text
        as="span"
        size="base"
        weight="medium"
        color="soft"
        className="min-w-0 break-words"
      >
        {label}
      </Text>
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        {pill}
      </div>
    </div>
  );
};

const RetryRow = ({
  label,
  count,
}: {
  label: string;
  count: number | boolean;
}) => {
  const isBoolean = typeof count === 'boolean';
  return (
    <div className="flex flex-wrap items-center justify-between px-4 py-2 gap-2.5 sm:gap-3">
      <Text
        as="span"
        size="md"
        weight="medium"
        color="muted"
        className="min-w-0 break-words"
      >
        {label}
      </Text>
      {isBoolean ? (
        count ? (
          <FaCheckCircle className="h-3.5 w-3.5 text-emerald-500" />
        ) : (
          <FaTimesCircle className="h-3.5 w-3.5 text-red-500" />
        )
      ) : (
        <PillCell value={String(count)} config={C.gray} />
      )}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
interface AlertStatusCardProps {
  title: typeof CardTitles.CLIENT | typeof CardTitles.PARTNER;
  statusData: ClientStatus | PartnerStatus;
  partnerInfo: { partnerProcessedStatus: any; partnerPatchStatus: any };
  clientInfo: { clientProcessedStatus: any; clientPatchStatus: any };
}
const STATUS_COLORS = {
  SUCCESS: 'green',
  LOADER: 'indigo',
  HYPHEN: 'indigo',
  FAIL: 'red',
};

export const AlertStatusCard = ({
  title,
  statusData,
  partnerInfo,
  clientInfo,
}: AlertStatusCardProps) => {
  const { ACTIONS } = COMMON_STEPPER_CONTENT;
  const isAccepted = statusData.accepted ? statusData.accepted : false; // Default to false if accepted field is missing
  const isProcessed = statusData.processed;
  const isPatched = statusData.patch;
  const statusCode = statusData?.statusCode;
  const sc = statusData?.statusCode;
  const { patchRetries, updatesyncRetries5XX } = statusData as ClientStatus;

  const { partnerProcessedStatus, partnerPatchStatus } = partnerInfo;
  const { clientProcessedStatus, clientPatchStatus } = clientInfo;

  // ── Processed icon ────────────────────────────────────────────────────
  const getProcIcon = () => {
    const processedIcon = {
      icon: StatusIconType.HYPHEN,
      color: STATUS_COLORS.HYPHEN,
      status: '-',
    };
    const processedStatus =
      title === CardTitles.PARTNER
        ? partnerProcessedStatus
        : clientProcessedStatus;
    if (!isAccepted && !isProcessed) {
      // If accepted is false and not processed, show hyphen
      processedIcon.icon = StatusIconType.HYPHEN;
      processedIcon.color = STATUS_COLORS.HYPHEN;
      processedIcon.status = 'Not started';
    }
    if (isAccepted && !isProcessed) {
      // If accepted is true but not processed, show loader
      processedIcon.icon = StatusIconType.LOADER;
      processedIcon.color = STATUS_COLORS.LOADER;
      processedIcon.status = 'Loading..';
    }
    if (isAccepted && isProcessed && processedStatus) {
      // If accepted and processed but not patched, show loader for patch step
      processedIcon.icon = StatusIconType.SUCCESS;
      processedIcon.color = STATUS_COLORS.SUCCESS;
      processedIcon.status = 'YES';
    }
    if (isAccepted && isProcessed && !processedStatus) {
      // If accepted and processed but not patched and status code is not success, show fail
      processedIcon.icon = StatusIconType.FAIL;
      processedIcon.color = STATUS_COLORS.FAIL;
      processedIcon.status = 'NO';
    }
    return processedIcon;
  };
  // ── Patch icon ─────────────────────────────────────────────────────────
  const getPatchIcon = () => {
    const patchIcon = {
      icon: StatusIconType.HYPHEN,
      color: STATUS_COLORS.HYPHEN,
      status: 'Not Started',
    };
    if (isProcessed) {
      if (patchRetries === 0 && updatesyncRetries5XX === 0) {
        patchIcon.icon = StatusIconType.HYPHEN;
        patchIcon.color = STATUS_COLORS.HYPHEN;
        patchIcon.status = 'Not Started';
      }
      if (!isPatched && (patchRetries > 0 || updatesyncRetries5XX > 0)) {
        patchIcon.icon = StatusIconType.LOADER;
        patchIcon.color = STATUS_COLORS.LOADER;
        patchIcon.status = 'Loading...';
      }
      if (isPatched && statusCode === STATUS_CODES.SUCCESS) {
        patchIcon.icon = StatusIconType.SUCCESS;
        patchIcon.color = STATUS_COLORS.SUCCESS;
        patchIcon.status = 'YES';
      }
      if (isPatched && statusCode !== STATUS_CODES.SUCCESS) {
        patchIcon.icon = StatusIconType.FAIL;
        patchIcon.color = STATUS_COLORS.FAIL;
        patchIcon.status = 'NO';
      }
      return patchIcon;
    }
    return patchIcon;
  };
  const processedIcon = getProcIcon();
  const patchIcon = getPatchIcon();

  const statusCodeColorMap: Record<number, 'green' | 'red' | 'gray'> = {
    200: 'green',
    201: 'green',
    422: 'red',
    500: 'red',
    0: 'gray',
    100: 'green',
  };
  const procColor: 'green' | 'indigo' | 'red' = isProcessed
    ? 'green'
    : (title === CardTitles.CLIENT
          ? clientProcessedStatus
          : partnerProcessedStatus) === ACTIONS.STATUS.NOT_STARTED
      ? 'indigo'
      : 'red';
  const patchColor: 'green' | 'indigo' | 'red' = isPatched
    ? 'green'
    : (title === CardTitles.CLIENT ? clientPatchStatus : partnerPatchStatus) ===
        ACTIONS.STATUS.NOT_STARTED
      ? 'indigo'
      : 'red';

  const isPartner = title === CardTitles.PARTNER;
  const CardIcon = isPartner ? (
    <GrServerCluster className="h-3.5 w-3.5 text-white" />
  ) : (
    <AiOutlineAudit className="h-3.5 w-3.5 text-white" />
  );

  return (
    <SectionCard title={title} icon={CardIcon}>
      <StatusRow
        label={ORGANISM_PROPS.ALERT_STATUS_CARD.LABEL_PROCESSED}
        icon={<StatusIcon type={processedIcon.icon} />}
        pill={<PillCell value={processedIcon.status} config={C[procColor]} />}
      />
      {'patch' in statusData && (
        <StatusRow
          label={ORGANISM_PROPS.ALERT_STATUS_CARD.LABEL_PATCH}
          icon={<StatusIcon type={patchIcon.icon} />}
          pill={<PillCell value={patchIcon.status} config={C[patchColor]} />}
        />
      )}
      <StatusRow
        label={ORGANISM_PROPS.ALERT_STATUS_CARD.LABEL_STATUS_CODE}
        pill={<PillCell value={sc ?? 0} preset="statusCode" />}
      />
      {'processedRetries' in statusData && (
        <RetryRow
          label={RetryLabels.PROCESSED_RETRIES}
          count={(statusData as ClientStatus).processedRetries}
        />
      )}
      {'patchRetries' in statusData && (
        <RetryRow
          label={RetryLabels.PATCH_RETRIES}
          count={(statusData as ClientStatus).patchRetries}
        />
      )}
      <RetryRow
        label={RetryLabels.CREATE_SYNC_5XX_RETRIES}
        count={statusData.createsyncRetries5XX || 0}
      />
      {'getsyncRetries5XX' in statusData && (
        <RetryRow
          label={RetryLabels.GET_SYNC_5XX_RETRIES}
          count={(statusData as ClientStatus).getsyncRetries5XX || 0}
        />
      )}
      <RetryRow
        label={RetryLabels.UPDATE_SYNC_5XX_RETRIES}
        count={statusData.updatesyncRetries5XX || 0}
      />
    </SectionCard>
  );
};
